import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ShareBar from '../components/ShareBar';
import SEO from '../components/SEO';
import Disclaimer from '../components/Disclaimer';

// Annual returns data: major indexes + key sectors
// Sources: S&P 500 sector indices, Bloomberg, Morningstar
const INDEXES = [
  { key: 'sp500',      label: 'S&P 500',     emoji: '📈', desc: 'Large-cap US stocks' },
  { key: 'nasdaq',     label: 'NASDAQ',      emoji: '💻', desc: 'Tech-heavy index' },
  { key: 'dow-jones',  label: 'Dow Jones',   emoji: '🏭', desc: '30 blue-chip stocks' },
  { key: 'bitcoin',    label: 'Bitcoin',     emoji: '₿',  desc: 'Crypto benchmark' },
];

// Sector returns — S&P 500 sector indices (SPDR ETF proxies)
// XLK=Tech, XLF=Financials, XLE=Energy, XLV=Healthcare, XLI=Industrials, XLY=Consumer Disc, XLP=Consumer Staples, XLU=Utilities, XLRE=Real Estate, XLB=Materials, XLC=Communication
const SECTORS = {
  technology:         { label: 'Technology',         emoji: '💾', returns: { 2015: 4.3, 2016: 12.7, 2017: 37.4, 2018: -1.6, 2019: 48.0, 2020: 43.4, 2021: 32.7, 2022: -28.2, 2023: 56.4, 2024: 36.6, 2025: -10.2 } },
  financials:         { label: 'Financials',         emoji: '🏦', returns: { 2015: -3.5, 2016: 22.8, 2017: 20.9, 2018: -14.7, 2019: 32.1, 2020: -4.1, 2021: 35.0, 2022: -12.4, 2023: 12.2, 2024: 30.7, 2025: 0.5 } },
  energy:             { label: 'Energy',             emoji: '⛽', returns: { 2015: -23.6, 2016: 27.4, 2017: -1.0, 2018: -20.5, 2019: 11.8, 2020: -37.3, 2021: 54.6, 2022: 65.7, 2023: -4.6, 2024: 5.4, 2025: -2.3 } },
  healthcare:         { label: 'Healthcare',         emoji: '🏥', returns: { 2015: 6.9, 2016: -4.3, 2017: 22.1, 2018: 6.4, 2019: 20.8, 2020: 13.5, 2021: 26.1, 2022: -2.1, 2023: 2.1, 2024: -0.9, 2025: -8.4 } },
  industrials:        { label: 'Industrials',        emoji: '🔧', returns: { 2015: -4.1, 2016: 18.9, 2017: 21.0, 2018: -13.3, 2019: 29.4, 2020: 11.3, 2021: 21.1, 2022: -5.5, 2023: 18.1, 2024: 16.4, 2025: -2.6 } },
  consumerDisc:       { label: 'Consumer Disc.',     emoji: '🛍️', returns: { 2015: 10.1, 2016: 6.0, 2017: 22.7, 2018: -0.2, 2019: 27.9, 2020: 33.3, 2021: 24.1, 2022: -37.6, 2023: 42.0, 2024: 30.4, 2025: -5.8 } },
  consumerStaples:    { label: 'Consumer Staples',   emoji: '🛒', returns: { 2015: 7.0, 2016: 5.4, 2017: 13.5, 2018: -8.4, 2019: 27.6, 2020: 10.8, 2021: 18.6, 2022: -0.6, 2023: -2.2, 2024: 14.8, 2025: 6.2 } },
  utilities:          { label: 'Utilities',          emoji: '⚡', returns: { 2015: -8.4, 2016: 16.3, 2017: 12.1, 2018: 4.1, 2019: 26.3, 2020: 0.5, 2021: 17.7, 2022: 1.6, 2023: -7.1, 2024: 23.4, 2025: 4.8 } },
  realEstate:         { label: 'Real Estate',        emoji: '🏠', returns: { 2015: 2.7, 2016: 3.4, 2017: 10.9, 2018: -5.6, 2019: 28.7, 2020: -4.7, 2021: 46.2, 2022: -26.2, 2023: 12.5, 2024: 5.2, 2025: 0.9 } },
  materials:          { label: 'Materials',          emoji: '⛏️', returns: { 2015: -8.5, 2016: 16.7, 2017: 22.1, 2018: -14.7, 2019: 24.6, 2020: 20.7, 2021: 27.7, 2022: -12.3, 2023: 12.0, 2024: -1.1, 2025: -4.6 } },
  communication:      { label: 'Communication',      emoji: '📡', returns: { 2015: 3.4, 2016: 2.9, 2017: null, 2018: -12.5, 2019: 32.7, 2020: 23.6, 2021: 21.6, 2022: -39.9, 2023: 55.8, 2024: 40.5, 2025: -7.1 } },
  internationalDev:   { label: 'Intl Developed',     emoji: '🌍', returns: { 2015: -0.8, 2016: 2.7, 2017: 25.0, 2018: -13.8, 2019: 22.0, 2020: 7.8, 2021: 11.3, 2022: -14.5, 2023: 18.9, 2024: 3.8, 2025: 7.2 } },
  emergingMarkets:    { label: 'Emerging Markets',   emoji: '🌏', returns: { 2015: -14.9, 2016: 11.2, 2017: 37.3, 2018: -14.6, 2019: 18.4, 2020: 18.3, 2021: -2.5, 2022: -20.1, 2023: 9.8, 2024: 7.5, 2025: 4.1 } },
  bonds:              { label: 'US Bonds (AGG)',     emoji: '📋', returns: { 2015: 0.5, 2016: 2.6, 2017: 3.5, 2018: 0.0, 2019: 8.7, 2020: 7.5, 2021: -1.5, 2022: -13.0, 2023: 5.5, 2024: 1.3, 2025: 2.8 } },
  gold:               { label: 'Gold',               emoji: '🥇', returns: { 2015: -10.4, 2016: 8.1, 2017: 13.2, 2018: -2.1, 2019: 18.3, 2020: 24.1, 2021: -3.6, 2022: -0.3, 2023: 13.0, 2024: 27.3, 2025: 18.5 } },
};

const YEARS = [2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015];

function colorForReturn(r) {
  if (r === null || r === undefined) return '#475569';
  if (r >= 30) return '#22c55e';
  if (r >= 10) return '#4ade80';
  if (r >= 0)  return '#86efac';
  if (r >= -10) return '#fca5a5';
  return '#f87171';
}

function fmtR(r) {
  if (r === null || r === undefined) return '—';
  return `${r > 0 ? '+' : ''}${r}%`;
}

const TABS = ['Indexes & Crypto', 'Sectors', 'Global & Bonds'];
const SECTOR_GROUPS = {
  'Indexes & Crypto': null,
  'Sectors': ['technology','financials','energy','healthcare','industrials','consumerDisc','consumerStaples','utilities','realEstate','materials','communication'],
  'Global & Bonds': ['internationalDev','emergingMarkets','bonds','gold'],
};

const TAB_KEYS = { 'Indexes & Crypto': 'indexes', 'Sectors': 'sectors', 'Global & Bonds': 'global' };
const TAB_FROM_KEY = Object.fromEntries(Object.entries(TAB_KEYS).map(([k,v]) => [v, k]));

export default function MarketReturnsHub() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const [tab, setTab] = useState(TAB_FROM_KEY[params.get('tab')] || 'Indexes & Crypto');
  const [selectedYear, setSelectedYear] = useState(params.get('year') ? parseInt(params.get('year')) : null);

  const sectorKeys = SECTOR_GROUPS[tab];

  const updateUrl = (newTab, newYear) => {
    const p = new URLSearchParams();
    const tabKey = TAB_KEYS[newTab];
    if (tabKey !== 'indexes') p.set('tab', tabKey);
    if (newYear) p.set('year', newYear);
    navigate({ search: p.toString() }, { replace: true });
  };
  const handleTab = (t) => { setTab(t); updateUrl(t, selectedYear); };
  const handleYear = (y) => { const ny = y === selectedYear ? null : y; setSelectedYear(ny); updateUrl(tab, ny); };

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setTab(TAB_FROM_KEY[p.get('tab')] || 'Indexes & Crypto');
    setSelectedYear(p.get('year') ? parseInt(p.get('year')) : null);
  }, [location.search]);

  const shareUrl = () => {
    const p = new URLSearchParams();
    const tabKey = TAB_KEYS[tab];
    if (tabKey !== 'indexes') p.set('tab', tabKey);
    if (selectedYear) p.set('year', selectedYear);
    return `/market-returns${p.toString() ? '?' + p.toString() : ''}`;
  };
  const shareLabel = () => {
    const yr = selectedYear ? ` in ${selectedYear}` : ' (2015–2025)';
    const t = tab === 'Indexes & Crypto' ? 'S&P 500, NASDAQ, Bitcoin' : tab === 'Sectors' ? 'all 11 stock market sectors' : 'international markets & bonds';
    return `Annual returns for ${t}${yr} — KnowFi`;
  };

  return (
    <div style={s.page}>
      <SEO
        title="Annual Market Returns by Year — All Sectors | KnowFi"
        description="Annual returns for the S&P 500, NASDAQ, Dow Jones, Bitcoin, all 11 stock market sectors, international markets, bonds, and gold from 2015–2025."
        path="/market-returns"
        keywords="annual market returns, S&P 500 returns by year, stock market returns by year, sector returns, NASDAQ returns history, Bitcoin annual returns, market performance by year, sector performance 2024 2025"
      />

      <div style={s.glow} />
      <div style={s.inner}>

        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <Link to="/" style={s.breadLink}>Home</Link>
          <span style={s.breadSep}>›</span>
          <Link to="/blog?tab=data" style={s.breadLink}>Data & Tools</Link>
          <span style={s.breadSep}>›</span>
          <span style={s.breadCurrent}>Market Returns</span>
        </div>

        {/* Header */}
        <div style={s.header}>
          <div style={s.badge}>📊 Market Data</div>
          <h1 style={s.h1}>Annual Market Returns</h1>
          <p style={s.subtitle}>
            How did every major index, sector, and asset class perform each year? See S&P 500, NASDAQ, 
            all 11 stock sectors, international markets, bonds, and Bitcoin — 2015 through 2025.
          </p>
        </div>

        {/* Year filter chips */}
        <div style={s.yearRow}>
          <button onClick={() => handleYear(null)} style={{ ...s.yearChip, ...(selectedYear === null ? s.yearChipActive : {}) }}>All Years</button>
          {YEARS.map(y => (
            <button key={y} onClick={() => handleYear(y)} style={{ ...s.yearChip, ...(selectedYear === y ? s.yearChipActive : {}) }}>
              {y}
            </button>
          ))}
        </div>

        {/* Tab bar */}
        <div style={s.tabBar}>
          {TABS.map(t => (
            <button key={t} onClick={() => handleTab(t)} style={{ ...s.tabBtn, ...(tab === t ? s.tabBtnActive : {}) }}>
              {t}
            </button>
          ))}
        </div>

        {/* INDEXES tab */}
        {tab === 'Indexes & Crypto' && (
          <div>
            <div style={{ margin: '16px 0 24px' }}>
              <ShareBar text={shareLabel()} url={`https://knowfi.io${shareUrl()}`} />
            </div>
            {INDEXES.map(idx => {
              const yearsToShow = selectedYear ? [selectedYear] : YEARS;
              return (
                <div key={idx.key} style={s.indexBlock}>
                  <div style={s.indexHeader}>
                    <span style={s.indexEmoji}>{idx.emoji}</span>
                    <div>
                      <div style={s.indexName}>{idx.label}</div>
                      <div style={s.indexDesc}>{idx.desc}</div>
                    </div>
                  </div>
                  <div style={s.returnRow}>
                    {yearsToShow.map(y => {
                      // pull from our static data inline
                      const data = INLINE_RETURNS[idx.key]?.[y];
                      return (
                        <Link key={y} to={`/market-returns/${idx.key}/${y}`} style={s.returnCell}>
                          <div style={s.returnYear}>{y}</div>
                          <div style={{ ...s.returnVal, color: colorForReturn(data) }}>{fmtR(data)}</div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* SECTORS or GLOBAL tabs */}
        {tab !== 'Indexes & Crypto' && (
          <div style={s.sectorTable}>
            <div style={{ margin: '16px 0 24px' }}>
              <ShareBar text={shareLabel()} url={`https://knowfi.io${shareUrl()}`} />
            </div>
            {/* Header row */}
            <div style={s.tableHeaderRow}>
              <div style={s.colLabel}>Asset</div>
              {(selectedYear ? [selectedYear] : YEARS).map(y => (
                <div key={y} style={s.colYear}>{y}</div>
              ))}
            </div>
            {sectorKeys.map(key => {
              const sec = SECTORS[key];
              const yearsToShow = selectedYear ? [selectedYear] : YEARS;
              return (
                <div key={key} style={s.tableRow}>
                  <div style={s.colLabel}>
                    <span style={s.secEmoji}>{sec.emoji}</span>
                    <span style={s.secLabel}>{sec.label}</span>
                  </div>
                  {yearsToShow.map(y => {
                    const r = sec.returns[y];
                    return (
                      <div key={y} style={{ ...s.colReturn, color: colorForReturn(r) }}>
                        {fmtR(r)}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* Best/worst year callout */}
        {tab === 'Indexes & Crypto' && !selectedYear && (
          <div style={s.insightRow}>
            <div style={s.insightBox}>
              <div style={s.insightTitle}>🏆 Best Recent Year</div>
              <div style={s.insightLine}><span>S&P 500:</span><span style={{ color: '#22c55e' }}>2019 +31.5%</span></div>
              <div style={s.insightLine}><span>NASDAQ:</span><span style={{ color: '#22c55e' }}>2023 +43.4%</span></div>
              <div style={s.insightLine}><span>Bitcoin:</span><span style={{ color: '#22c55e' }}>2017 +1,369%</span></div>
            </div>
            <div style={s.insightBox}>
              <div style={s.insightTitle}>📉 Worst Recent Year</div>
              <div style={s.insightLine}><span>S&P 500:</span><span style={{ color: '#f87171' }}>2022 -18.1%</span></div>
              <div style={s.insightLine}><span>NASDAQ:</span><span style={{ color: '#f87171' }}>2022 -32.5%</span></div>
              <div style={s.insightLine}><span>Bitcoin:</span><span style={{ color: '#f87171' }}>2018 -72.6%</span></div>
            </div>
            <div style={s.insightBox}>
              <div style={s.insightTitle}>📊 Long-Term Avg</div>
              <div style={s.insightLine}><span>S&P 500:</span><span style={{ color: '#38bdf8' }}>~10% / yr</span></div>
              <div style={s.insightLine}><span>NASDAQ:</span><span style={{ color: '#38bdf8' }}>~13.5% / yr</span></div>
              <div style={s.insightLine}><span>Dow Jones:</span><span style={{ color: '#38bdf8' }}>~9.4% / yr</span></div>
            </div>
          </div>
        )}

        <div style={s.note}>
          Returns shown are approximate annual total returns (price + dividends where applicable).
          Sector returns reflect S&P 500 GICS sector indices. Past performance does not guarantee future results.
        </div>

        {/* CTAs */}
        <div style={s.ctaSection}>
          <div style={s.ctaTitle}>Put returns in context</div>
          <div style={s.ctaRow}>
            <Link to="/calculators/investment-growth" style={s.ctaBtn}>📈 Investment Calculator</Link>
            <Link to="/ask" style={s.ctaBtnSecondary}>🤖 Ask the AI</Link>
          </div>
        </div>

        <Disclaimer />
      </div>
    </div>
  );
}

// Inline returns for index cards (matches marketReturns.js)
const INLINE_RETURNS = {
  sp500:       { 2015: 1.4, 2016: 12.0, 2017: 21.8, 2018: -4.4, 2019: 31.5, 2020: 18.4, 2021: 28.7, 2022: -18.1, 2023: 26.3, 2024: 25.0, 2025: -4.0 },
  nasdaq:      { 2015: 5.7, 2016: 7.5,  2017: 29.6, 2018: -3.9, 2019: 36.7, 2020: 44.9, 2021: 21.4, 2022: -32.5, 2023: 43.4, 2024: 29.6, 2025: -8.0 },
  'dow-jones': { 2015: -2.2, 2016: 16.5, 2017: 28.1, 2018: -5.6, 2019: 25.3, 2020: 9.7, 2021: 21.0, 2022: -8.8, 2023: 16.2, 2024: 15.0, 2025: -1.5 },
  bitcoin:     { 2015: 35.2, 2016: 124.6, 2017: 1369.0, 2018: -72.6, 2019: 92.4, 2020: 302.8, 2021: 59.8, 2022: -64.3, 2023: 154.7, 2024: 121.4, 2025: -15.0 },
};

const s = {
  page:     { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '40px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:     { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '800px', height: '400px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.06) 0%, transparent 65%)', pointerEvents: 'none' },
  inner:    { maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 },

  breadcrumb:   { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px' },
  breadLink:    { color: '#475569', textDecoration: 'none', fontSize: '13px' },
  breadSep:     { color: '#334155', fontSize: '13px' },
  breadCurrent: { color: '#64748b', fontSize: '13px' },

  header:   { marginBottom: '28px' },
  badge:    { display: 'inline-block', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.25)', color: '#38bdf8', fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' },
  h1:       { fontSize: 'clamp(24px, 4vw, 38px)', fontWeight: '900', margin: '0 0 12px', letterSpacing: '-0.5px', lineHeight: 1.2, color: '#f1f5f9' },
  subtitle: { fontSize: '16px', color: '#64748b', margin: 0, lineHeight: 1.65, maxWidth: '720px' },

  yearRow:      { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' },
  yearChip:     { background: '#1e293b', border: '1px solid #334155', color: '#475569', padding: '5px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  yearChipActive: { background: 'rgba(56,189,248,0.15)', border: '1px solid #38bdf8', color: '#38bdf8' },

  tabBar:       { display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #334155', paddingBottom: '12px' },
  tabBtn:       { background: 'transparent', border: 'none', color: '#475569', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  tabBtnActive: { background: 'rgba(56,189,248,0.1)', color: '#38bdf8' },

  indexBlock:   { background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', padding: '18px 20px', marginBottom: '12px' },
  indexHeader:  { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' },
  indexEmoji:   { fontSize: '26px' },
  indexName:    { fontSize: '16px', fontWeight: '800', color: '#f1f5f9' },
  indexDesc:    { fontSize: '12px', color: '#475569', marginTop: '2px' },
  returnRow:    { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  returnCell:   { background: '#0f172a', borderRadius: '8px', padding: '10px 14px', textDecoration: 'none', minWidth: '72px', textAlign: 'center' },
  returnYear:   { fontSize: '11px', color: '#475569', fontWeight: '600', marginBottom: '4px' },
  returnVal:    { fontSize: '14px', fontWeight: '800' },

  sectorTable:     { background: '#1e293b', borderRadius: '12px', border: '1px solid #334155', overflow: 'auto', marginBottom: '24px' },
  tableHeaderRow:  { display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #334155', gap: '0' },
  tableRow:        { display: 'flex', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #1e293b' },
  colLabel:        { display: 'flex', alignItems: 'center', gap: '8px', minWidth: '180px', flex: '0 0 180px', fontSize: '13px', color: '#64748b', fontWeight: '600' },
  colYear:         { flex: 1, minWidth: '68px', textAlign: 'center', fontSize: '11px', color: '#475569', fontWeight: '700', textTransform: 'uppercase' },
  colReturn:       { flex: 1, minWidth: '68px', textAlign: 'center', fontSize: '13px', fontWeight: '800' },
  secEmoji:        { fontSize: '16px' },
  secLabel:        { fontSize: '13px', color: '#94a3b8', fontWeight: '600' },

  insightRow:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' },
  insightBox:   { background: '#1e293b', borderRadius: '10px', border: '1px solid #334155', padding: '16px' },
  insightTitle: { fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '10px' },
  insightLine:  { display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#94a3b8', marginBottom: '6px', fontWeight: '600' },

  note:    { fontSize: '12px', color: '#334155', marginBottom: '24px', lineHeight: 1.6 },

  ctaSection:      { background: 'rgba(56,189,248,0.04)', border: '1px solid rgba(56,189,248,0.12)', borderRadius: '14px', padding: '24px', textAlign: 'center', marginBottom: '32px' },
  ctaTitle:        { fontSize: '17px', fontWeight: '700', color: '#f1f5f9', marginBottom: '16px' },
  ctaRow:          { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  ctaBtn:          { background: '#38bdf8', color: '#0f172a', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '800', fontSize: '14px' },
  ctaBtnSecondary: { background: '#1e293b', color: '#f1f5f9', padding: '12px 24px', borderRadius: '8px', textDecoration: 'none', fontWeight: '700', fontSize: '14px', border: '1px solid #334155' },
};
